/**
 * MobileAiCaptureSheet — the bottom-sheet UI that the FAB opens.
 *
 * Pure presentation: gets its query/answer/etc state via props.
 * Used by MobileAiCaptureFab as the body of its open sheet.
 *
 * Self-contained pieces (header, input, suggestions, response
 * area, proposals, action chips, result rows) so future tweaks
 * to any single section don't touch the others.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, X, Send, AlertCircle, Check, Clock3, HelpCircle, ArrowUpRight, ClipboardPaste, Inbox as InboxIcon, Bot } from 'lucide-react';

const ACTION_VERB = { mark_done: 'Mark done', snooze: 'Snooze', delete: 'Delete' };
const ACTION_ICON = { mark_done: Check, snooze: Clock3 };

// Clipboard API feature detection — `readText` is the gated bit;
// `navigator.clipboard` exists on insecure contexts but the read
// methods don't. Done once at module load — won't change without
// a page refresh.
const CLIPBOARD_READ_SUPPORTED =
  typeof navigator !== 'undefined' &&
  navigator.clipboard &&
  typeof navigator.clipboard.readText === 'function';

/**
 * PasteChip — "📋 Paste" affordance that reads the clipboard inside
 * the click event (satisfying browsers' user-gesture requirement)
 * and pipes the text into the AI sheet's input via onPaste.
 *
 * Why not pre-read on mount and show a preview? Reading the
 * clipboard ALWAYS prompts on iOS Safari + some Android browsers,
 * even silently on mount. Pre-reading would trigger a permission
 * banner the moment the FAB opens — terrible UX. Reading inside
 * the click bypasses the prompt (the click IS the consent).
 *
 * Feature-detection: silently renders nothing when clipboard.readText
 * is unavailable (insecure context, older browsers, etc.). Better
 * than rendering a dead button.
 */
function PasteChip({ onPaste }) {
  const [busy, setBusy] = useState(false);

  if (!CLIPBOARD_READ_SUPPORTED) return null;

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const text = await navigator.clipboard.readText();
      const trimmed = (text || '').trim();
      if (!trimmed) {
        toast.message('Clipboard is empty');
        return;
      }
      // Cap to 800 chars — anything longer is almost certainly a
      // copy-paste accident (e.g. an entire article). The user can
      // still extend or trim in the input.
      onPaste(trimmed.length > 800 ? trimmed.slice(0, 800) : trimmed);
    } catch (err) {
      // The most common failure is a permission denial. Don't log
      // it as an error; the chip is best-effort by design.
      toast.error('Couldn\'t read clipboard');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      data-testid="mobile-ai-fab-paste-chip"
      onClick={handleClick}
      disabled={busy}
      className="pd-no-touch-min text-[11px] rounded-full px-2.5 py-1 inline-flex items-center gap-1 transition-colors"
      style={{
        background: 'var(--pd-focus-soft, var(--pd-panel-2))',
        border: '1px solid var(--pd-focus)',
        color: 'var(--pd-focus)',
        opacity: busy ? 0.6 : 1,
        cursor: busy ? 'wait' : 'pointer',
      }}
      title="Paste from clipboard"
      aria-label="Paste clipboard contents into the input"
    >
      <ClipboardPaste className="w-3 h-3" />
      {busy ? 'Reading…' : 'Paste'}
    </button>
  );
}

// A handful of starter prompts that hint at what the bar can do.
// Tapping populates the input — the user can edit before submitting.
// Keep these short and verb-led; long suggestions wrap awkwardly on
// small screens.
const SUGGESTIONS = [
  'Add task for tomorrow',
  'Log call with',
  'Pay ₹',
  'Show today',
];

export function MobileAiCaptureSheet({
  open,
  onClose,
  inputRef,
  query,
  onQueryChange,
  onSubmit,
  loading,
  error,
  answer,
  proposals,
  onConfirmProposal,
  actions,
  results,
  onSelectResult,
  routeToInbox,
  onToggleRouteToInbox,
}) {
  // Auto-focus the input the moment the sheet opens. We delay slightly
  // so the bottom sheet's slide-up animation doesn't fight with the
  // keyboard's slide-up — focusing too early on iOS causes the keyboard
  // to jitter into place.
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 220);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, inputRef]);

  // Swipe-down-to-dismiss. Attached only to the drag-handle/header zone
  // (NOT the scrollable response area) so it never fights with content
  // scroll or input focus. Dragging the sheet down past ~80px releases
  // into a close; a shorter drag springs back. Makes the previously
  // decorative drag-handle a real iOS-style affordance.
  const [dragY, setDragY] = useState(0);
  const dragState = useRef({ startY: 0, active: false });
  const onDragStart = (e) => {
    const y = e.touches?.[0]?.clientY ?? e.clientY;
    dragState.current = { startY: y, active: true };
  };
  const onDragMove = (e) => {
    if (!dragState.current.active) return;
    const y = e.touches?.[0]?.clientY ?? e.clientY;
    const delta = y - dragState.current.startY;
    if (delta > 0) setDragY(delta);
  };
  const onDragEnd = () => {
    if (!dragState.current.active) return;
    dragState.current.active = false;
    if (dragY > 80) { onClose(); }
    setDragY(0);
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop — tap to dismiss without triggering anything below. */}
      <div
        data-testid="mobile-ai-fab-backdrop"
        className="pd-mobile-only fixed inset-0 z-[450]"
        style={{ background: 'rgba(15,17,23,0.4)', backdropFilter: 'blur(6px)' }}
        onClick={onClose}
      />

      {/* Bottom sheet */}
      <div
        data-testid="mobile-ai-fab-sheet"
        className="pd-mobile-only fixed left-0 right-0 z-[451] rounded-t-[28px] overflow-hidden animate-slide-up"
        style={{
          bottom: 0,
          background: 'var(--pd-panel-solid)',
          border: '1px solid var(--pd-border)',
          borderBottom: 'none',
          boxShadow: '0 -8px 32px rgba(15,17,23,0.20)',
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
          maxHeight: '78vh',
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: dragState.current.active ? 'none' : 'transform 220ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Drag-handle bar — iOS sheet affordance; swipe down to dismiss. */}
        <div
          data-testid="mobile-ai-fab-drag-handle"
          className="flex justify-center pt-2.5 pb-2 cursor-grab"
          style={{ touchAction: 'none' }}
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
          aria-hidden="true"
        >
          <div className="w-10 h-1.5 rounded-full" style={{ background: 'var(--pd-border)' }} />
        </div>

        {/* Header */}
        <div className="px-4 pt-1 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: 'var(--pd-focus)' }} />
            <span className="text-[12px] font-semibold uppercase tracking-[0.10em]" style={{ color: 'var(--pd-subtle)' }}>
              PolkDesQ AI
            </span>
            {/* Routing toggle — mirrors QuickAddModal's Type↔Inbox pill.
                In "Ask" mode (default) the Send button fires the AI
                agent; in "Inbox" mode it parks the text in the triage
                queue. Indigo when AI, amber when Inbox so the modal's
                colour echoes its destination (matches the existing
                Brain Dump amber affordance for "rapid capture flow"). */}
            {onToggleRouteToInbox ? (
              <button
                type="button"
                data-testid="mobile-ai-fab-route-toggle"
                onClick={onToggleRouteToInbox}
                className="ml-1 inline-flex items-center gap-1 px-3 rounded-full text-[10px] font-semibold transition-colors"
                style={{
                  minHeight: 44,
                  background: routeToInbox ? 'var(--pd-brand-soft, var(--pd-panel-2))' : 'var(--pd-focus-soft, var(--pd-panel-2))',
                  color: routeToInbox ? 'var(--pd-brand)' : 'var(--pd-focus)',
                  border: '1px solid ' + (routeToInbox ? 'var(--pd-brand)' : 'var(--pd-focus)'),
                }}
                title={
                  routeToInbox
                    ? 'Routing to Inbox (triage later). Click to send through AI agent instead.'
                    : 'Routing through AI agent (smart classify). Click to send to Inbox for later triage.'
                }
                aria-pressed={routeToInbox}
                aria-label={routeToInbox ? 'Switch routing to AI agent' : 'Switch routing to Inbox'}
              >
                {routeToInbox ? <InboxIcon className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                {routeToInbox ? 'Inbox' : 'Ask'}
              </button>
            ) : null}
          </div>
          <button
            data-testid="mobile-ai-fab-close"
            onClick={onClose}
            className="rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--pd-panel-2)', width: 44, height: 44 }}
            aria-label="Close"
          >
            <X className="w-4 h-4" style={{ color: 'var(--pd-muted)' }} />
          </button>
        </div>

        {/* Input — large, 16px font to prevent iOS auto-zoom. */}
        <div className="px-4 pb-3">
          <div
            className="flex items-center gap-2 rounded-2xl px-3 py-2.5"
            style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}
          >
            <input
              ref={inputRef}
              data-testid="mobile-ai-fab-input"
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
              placeholder={routeToInbox ? 'Capture to Inbox…' : 'Capture or ask anything…'}
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="sentences"
              spellCheck="true"
              enterKeyHint="send"
              className="flex-1 bg-transparent outline-none placeholder:text-[var(--pd-subtle)]"
              // 16px font is the iOS threshold below which mobile
              // Safari auto-zooms when the input gains focus — set
              // explicitly to dodge it.
              style={{ color: 'var(--pd-text)', fontSize: '16px' }}
            />
            <button
              data-testid="mobile-ai-fab-submit"
              onClick={onSubmit}
              disabled={!query.trim() || loading}
              className="rounded-xl flex items-center justify-center transition-all flex-shrink-0"
              style={{
                width: 44,
                height: 44,
                background: query.trim()
                  ? (routeToInbox ? 'var(--pd-brand)' : 'var(--pd-focus)')
                  : 'var(--pd-panel)',
                color: routeToInbox ? '#1A1300' : 'white',
                opacity: loading ? 0.6 : 1,
              }}
              aria-label={routeToInbox ? 'Save to Inbox' : 'Send to AI'}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>

          {/* Sample suggestions — only show when input is empty and
              we haven't yet gotten an answer. Tapping populates the
              input so the user can edit before sending. */}
          {!query && !answer && !loading ? (
            <div data-testid="mobile-ai-fab-suggestions" className="flex flex-wrap gap-1.5 mt-2.5">
              {/* Paste-from-clipboard chip — feature-gated on
                  `navigator.clipboard.readText`. Click reads the
                  clipboard inside the user gesture (so the browser's
                  permission policy is satisfied) and pre-fills the
                  input. Lives FIRST in the chip row so it's the
                  primary "I just copied something" affordance. */}
              <PasteChip onPaste={(text) => { onQueryChange(text); inputRef.current?.focus(); }} />
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`mobile-ai-fab-suggestion-${s.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => { onQueryChange(s + ' '); inputRef.current?.focus(); }}
                  className="pd-no-touch-min text-[11px] rounded-full px-2.5 py-1"
                  style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)', color: 'var(--pd-muted)' }}
                >
                  {s}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* Response area — answer + actions + proposals + results.
            Scrollable so it never exceeds 78vh. */}
        <div className="px-4 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(78vh - 140px)' }}>
          {loading ? (
            <div data-testid="mobile-ai-fab-loading" className="flex items-center gap-2 py-3">
              <div
                className="w-4 h-4 rounded-full animate-spin"
                style={{ border: '2px solid var(--pd-border)', borderTopColor: 'var(--pd-focus)' }}
              />
              <span className="text-[12px]" style={{ color: 'var(--pd-muted)' }}>Thinking…</span>
            </div>
          ) : null}

          {error ? (
            <div data-testid="mobile-ai-fab-error" className="flex items-start gap-2 py-2.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5" style={{ color: 'var(--pd-warning)' }} />
              <div className="text-[12px]" style={{ color: 'var(--pd-text)' }}>{error}</div>
            </div>
          ) : null}

          {answer ? (
            <div
              data-testid="mobile-ai-fab-answer"
              className="rounded-2xl p-3 mb-2"
              style={{ background: 'var(--pd-panel-2)', borderLeft: '3px solid var(--pd-focus)' }}
            >
              <div className="text-[13px] leading-relaxed" style={{ color: 'var(--pd-text)' }}>
                {answer}
              </div>
            </div>
          ) : null}

          {/* Proposal chips — large, single-column on mobile so each
              is comfortably tappable. */}
          {proposals.length > 0 ? (
            <div data-testid="mobile-ai-fab-proposals" className="space-y-1.5 mb-2">
              <div className="flex items-center gap-1.5 mb-1">
                <HelpCircle className="w-3 h-3" style={{ color: 'var(--pd-warning)' }} />
                <span className="text-[9px] uppercase tracking-[0.10em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>
                  Did you mean…?
                </span>
              </div>
              {proposals.map((p, i) => {
                const Icon = ACTION_ICON[p.action] || Sparkles;
                return (
                  <button
                    key={`${p.kind}-${p.id}-${i}`}
                    data-testid={`mobile-ai-fab-proposal-${i}`}
                    onClick={() => onConfirmProposal(p)}
                    className="w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-left"
                    style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}
                  >
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--pd-text)' }} />
                    <span className="text-[12px] flex-1 truncate" style={{ color: 'var(--pd-text)' }}>
                      {ACTION_VERB[p.action] || p.action} · {p.title}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}

          {/* Mutation chips — collapse to a single line per chip. */}
          {actions.length > 0 ? (
            <div data-testid="mobile-ai-fab-actions" className="flex flex-wrap gap-1.5 mb-2">
              {actions.map((a, i) => (
                <span
                  key={`${a.kind}-${a.id || i}`}
                  data-testid={`mobile-ai-fab-action-${i}`}
                  className="text-[11px] rounded-full px-2.5 py-1 inline-flex items-center gap-1.5 font-semibold"
                  style={{ background: 'var(--pd-success-soft, var(--pd-panel-2))', color: 'var(--pd-success, var(--pd-text))', border: '1px solid var(--pd-border)' }}
                >
                  ✓ {a.label || `${a.kind} updated`}
                </span>
              ))}
            </div>
          ) : null}

          {/* Result rows — tap to deep-link. Show top 5 only on
              mobile (the rest are still searchable via the bar). */}
          {results.length > 0 ? (
            <div data-testid="mobile-ai-fab-results" className="space-y-1">
              {results.slice(0, 5).map((item) => (
                <button
                  key={item.id || item.title}
                  data-testid={`mobile-ai-fab-result-${item.id}`}
                  onClick={() => onSelectResult(item)}
                  className="w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-left"
                  style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] uppercase tracking-[0.08em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>
                      {item.type}
                    </div>
                    <div className="text-[12px] truncate" style={{ color: 'var(--pd-text)' }}>
                      {item.title || '(untitled)'}
                    </div>
                  </div>
                  <ArrowUpRight className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--pd-subtle)' }} />
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
